import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { ExecutiveDashboard } from "./pages/ExecutiveDashboard";
import { SalesPage } from "./pages/SalesPage";
import { FinancePage } from "./pages/FinancePage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectsModulePage } from "./pages/ProjectsModulePage";
import { TasksPage } from "./pages/TasksPage";
import { GrantsPage } from "./pages/GrantsPage";
import { RisksPage } from "./pages/RisksPage";
import { AIAssistant } from "./pages/AIAssistant";
import { AccountingPage } from "./pages/AccountingPage";
import { AccountingWorkspace } from "./pages/AccountingWorkspace";
import BookkeepingPage from "./pages/BookkeepingPage";
import TravelPage from "./pages/TravelPage";
import { PurchaseOrdersPage } from "./pages/PurchaseOrdersPage";
import { CalendarPage } from "./pages/CalendarPage";
import { ClientsPage } from "./pages/ClientsPage";
import { ServicesPage } from "./pages/ServicesPage";
import { SocialConfigPage } from "./pages/SocialConfigPage";
import { TeamManagementPage } from "./pages/TeamManagementPage";
import { PermissionsPage } from "./pages/PermissionsPage";
import { AuditTrailPage } from "./pages/AuditTrailPage";
import { HRMPage } from "./pages/HRMPage";
import { RepositoryPage } from "./pages/RepositoryPage";
import { StudentBillingPage } from "./pages/StudentBillingPage";
import { WFHTrackerPage } from "./pages/WFHTrackerPage";
import { MyWorkPage } from "./pages/MyWorkPage";
import { SharedFilePage } from "./pages/SharedFilePage";
import SettingsPage from "./pages/SettingsPage";
import PayInvoicePage from "./pages/PayInvoicePage";
import CommandCentrePage from "./pages/CommandCentrePage";
import ApprovalsPage from "./pages/ApprovalsPage";
import CompliancePage from "./pages/CompliancePage";
import BizDevPage from "./pages/BizDevPage";
import LearnersPage from "./pages/LearnersPage";
import ReportsPage from "./pages/ReportsPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      <Route path="/pay/:token" element={<PayInvoicePage />} />

      {/* Protected Routes */}
      <Route path="/command-centre" element={
        <ProtectedRoute>
          <CommandCentrePage />
        </ProtectedRoute>
      } />

      <Route path="/approvals" element={
        <ProtectedRoute>
          <ApprovalsPage />
        </ProtectedRoute>
      } />

      <Route path="/compliance" element={
        <ProtectedRoute>
          <CompliancePage />
        </ProtectedRoute>
      } />

      <Route path="/bizdev" element={
        <ProtectedRoute>
          <BizDevPage />
        </ProtectedRoute>
      } />

      <Route path="/learners" element={
        <ProtectedRoute>
          <LearnersPage />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <ExecutiveDashboard />
        </ProtectedRoute>
      } />

      <Route path="/my-work" element={
        <ProtectedRoute>
          <MyWorkPage />
        </ProtectedRoute>
      } />
      
      <Route path="/sales" element={
        <ProtectedRoute>
          <SalesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/finance" element={
        <ProtectedRoute>
          <FinancePage />
        </ProtectedRoute>
      } />
      
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsModulePage />
        </ProtectedRoute>
      } />
      
      <Route path="/projects-legacy" element={
        <ProtectedRoute>
          <ProjectsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tasks" element={
        <ProtectedRoute>
          <TasksPage />
        </ProtectedRoute>
      } />
      
      <Route path="/grants" element={
        <ProtectedRoute>
          <GrantsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/risks" element={
        <ProtectedRoute>
          <RisksPage />
        </ProtectedRoute>
      } />
      
      <Route path="/ai" element={
        <ProtectedRoute>
          <AIAssistant />
        </ProtectedRoute>
      } />
      
      <Route path="/accounting" element={
        <ProtectedRoute>
          <AccountingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/accounting-workspace" element={
        <ProtectedRoute>
          <AccountingWorkspace />
        </ProtectedRoute>
      } />
      
      <Route path="/bookkeeping" element={
        <ProtectedRoute>
          <BookkeepingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/travel" element={
        <ProtectedRoute>
          <TravelPage />
        </ProtectedRoute>
      } />
      
      <Route path="/purchase-orders" element={
        <ProtectedRoute>
          <PurchaseOrdersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/services" element={
        <ProtectedRoute>
          <ServicesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/social-config" element={
        <ProtectedRoute>
          <SocialConfigPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/team" element={
        <ProtectedRoute>
          <TeamManagementPage />
        </ProtectedRoute>
      } />
      
      <Route path="/permissions" element={
        <ProtectedRoute>
          <PermissionsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/audit-trail" element={
        <ProtectedRoute>
          <AuditTrailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/hr" element={
        <ProtectedRoute>
          <HRMPage />
        </ProtectedRoute>
      } />
      
      <Route path="/repository" element={
        <ProtectedRoute>
          <RepositoryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/student-billing" element={
        <ProtectedRoute>
          <StudentBillingPage />
        </ProtectedRoute>
      } />

      <Route path="/wfh-tracker" element={
        <ProtectedRoute>
          <WFHTrackerPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      {/* Public route - no auth required */}
      <Route path="/shared/:shareToken" element={<SharedFilePage />} />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
